import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { defaultConfig } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { defaultChartViewerConfig } from "./default_chart_viewer_config";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useCustomer } from "../../contexts/app/provider";
import { commonRunEditState } from "../../entities/common_run_edit_state";
import {
  createSupplyCellContext,
  isSupportedSupplyCellRecipe,
  supplyCellPrompt,
  type CommonSupplyCellContextV1,
} from "../../entities/supply_cell_context";
import {
  renderedViewBindingIdentity,
  type CellExplanationCapture,
  type PivotCellExplanationConfig,
  type RenderedViewBinding,
} from "../../entities/pivot_view_config";
import {
  createRenderedDatePivotCellContext,
  isSupportedRenderedDatePivotCellContext,
  renderedDatePivotCellPrompt,
  type CommonRenderedCellContextV2,
} from "../../entities/rendered_cell_context";
import {
  createCommonViewContext,
  type CommonViewContextV1,
} from "../../entities/common_view_context";
import { isCommonCurrentViewReceiptId } from "../../contexts/chat/nucleus_sse_client";
import { isCommonNucleusExplainabilityCustomer } from "../../entities/common_nucleus_customer_capabilities";
import {
  awaitNucleusExplainStart,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_STALE_REASON,
  rejectNucleusExplainActivation,
} from "../../entities/nucleus_explain_action";
import { useSynchronizedPending } from "../../components/nucleus_explain/use_synchronized_pending";

type PendingSupplyCell = Readonly<{
  context: CommonSupplyCellContextV1 | CommonRenderedCellContextV2;
  viewContext?: CommonViewContextV1;
  prompt: string;
  runIdentity: string;
  sourceIdentity: string;
  receiptId?: string;
  connectionReady: boolean;
}>;

const getRunIdentity = (
  run: React.ContextType<typeof VersionedDataContext>["mainRun"] | undefined,
): string | undefined =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : undefined;

export const SupplyNoPerspectiveContainer = () => {
  const { selectedCustomerId } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    comparedRun = null,
  } = useContext(VersionedDataContext);
  const { isComparing, setIsSidebarOpen } = useContext(LayoutContext);
  const {
    connectChat,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    registerCurrentViewData,
    sendMessage,
    transportKind,
  } = useContext(ChatContext);
  const activeRun = mainRun || lastSuccessfulRun;
  const activeRunIdentity = getRunIdentity(activeRun);
  const activeRunIdentityRef = useRef(activeRunIdentity);
  activeRunIdentityRef.current = activeRunIdentity;
  const {
    pending: pendingSupplyCell,
    pendingRef: pendingSupplyCellRef,
    setPending: setPendingSupplyCell,
    clearPending: clearPendingSupplyCell,
    replacePending: replacePendingSupplyCell,
  } = useSynchronizedPending<PendingSupplyCell>();
  const currentRenderedSourceIdentityRef = useRef<string | undefined>(
    undefined,
  );
  const hasRenderedSourceSignalRef = useRef(false);
  const releaseCurrentViewDataRef = useRef<(() => void) | null>(null);

  const supportsSupplyCellExplain = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      commonRunEditState(activeRun.runSettings.settingsNameAndVersion) &&
      !isComparing &&
      comparedRun === null &&
      isCommonNucleusExplainabilityCustomer(
        activeRun.runSettings.organizationId,
      ) &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId),
  );
  const supportsSupplyCellExplainRef = useRef(supportsSupplyCellExplain);
  supportsSupplyCellExplainRef.current = supportsSupplyCellExplain;

  const handleRenderedSourceChange = useCallback(
    (source: RenderedViewBinding | undefined, viewParams?: DatePivotParams) => {
      hasRenderedSourceSignalRef.current = true;
      const sourceIdentity = source
        ? renderedViewBindingIdentity(source)
        : undefined;
      currentRenderedSourceIdentityRef.current = sourceIdentity;
      const pending = pendingSupplyCellRef.current;
      if (pending && pending.sourceIdentity !== sourceIdentity) {
        clearPendingSupplyCell(pending);
      }
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
      const viewContext = viewParams
        ? createCommonViewContext({
            surfaceId: "supply",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          })
        : undefined;
      if (
        supportsSupplyCellExplain &&
        registerCurrentViewData &&
        isCommonCurrentViewReceiptId(source?.receiptId) &&
        viewContext &&
        getRunIdentity(source.run) === activeRunIdentity
      ) {
        releaseCurrentViewDataRef.current = registerCurrentViewData(
          {
            version: "CommonCurrentViewData.v1",
            surfaceId: "supply",
            receiptId: source.receiptId,
          },
          viewContext,
        );
      }
    },
    [
      activeRunIdentity,
      clearPendingSupplyCell,
      pendingSupplyCellRef,
      registerCurrentViewData,
      supportsSupplyCellExplain,
    ],
  );

  useEffect(
    () => () => {
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
    },
    [],
  );

  const captureSupplyCell = useCallback<
    PivotCellExplanationConfig<DatePivotParams>["capture"]
  >(
    ({ columnKey, row, value, viewParams, source }) => {
      if (!supportsSupplyCellExplain || !activeRun) return undefined;
      const viewContext = createCommonViewContext({
        surfaceId: "supply",
        measure: viewParams.measure,
        dateHierarchy: viewParams.dateHierarchy,
        minDate: viewParams.minDate,
        maxDate: viewParams.maxDate,
        segments: viewParams.segments,
        filters: viewParams.filters,
        postFilters: viewParams.postFilters,
      });
      const renderedRunIdentity = getRunIdentity(source?.run);
      if (
        source?.receiptId &&
        activeRunIdentity &&
        renderedRunIdentity === activeRunIdentity
      ) {
        const renderedContext = createRenderedDatePivotCellContext({
          surfaceId: "supply",
          receiptId: source.receiptId,
          columnKey,
          segments: viewParams.segments,
          row,
          measure: viewParams.measure,
          dateField: viewParams.dateHierarchy,
          rawValue: value,
        });
        if (
          renderedContext &&
          isSupportedRenderedDatePivotCellContext(renderedContext)
        ) {
          return Object.freeze({
            prompt: renderedDatePivotCellPrompt(renderedContext, viewContext),
            metricContext: renderedContext,
            viewContext,
          });
        }
      }

      const context = createSupplyCellContext({
        measure: viewParams.measure,
        periodField: viewParams.dateHierarchy,
        monthStartEpochMs: Number(columnKey),
        segments: viewParams.segments,
        row,
        filters: viewParams.filters,
        postFilters: viewParams.postFilters ?? [],
        rawValue: value,
        settingsNameAndVersion: activeRun.runSettings.settingsNameAndVersion,
        comparison: isComparing,
      });
      if (!context || !isSupportedSupplyCellRecipe(context)) return undefined;
      return Object.freeze({
        prompt: supplyCellPrompt(context),
        metricContext: context,
        viewContext,
      });
    },
    [activeRun, activeRunIdentity, isComparing, supportsSupplyCellExplain],
  );

  const explainSupplyCell = useCallback(
    (capture: CellExplanationCapture) => {
      const context = capture.metricContext;
      const renderedRun = capture.source.run;
      const renderedRunIdentity = getRunIdentity(renderedRun);
      const sourceIdentity = renderedViewBindingIdentity(capture.source);
      const isDedicatedContext =
        context.version === "CommonSupplyCellContext.v1" &&
        isSupportedSupplyCellRecipe(context);
      const isRenderedContext =
        context.version === "CommonRenderedCellContext.v2" &&
        context.surfaceId === "supply" &&
        isSupportedRenderedDatePivotCellContext(context) &&
        context.receiptId === capture.source.receiptId;
      if (
        !supportsSupplyCellExplain ||
        (!isDedicatedContext && !isRenderedContext) ||
        !activeRun ||
        !activeRunIdentity ||
        !renderedRunIdentity ||
        renderedRunIdentity !== activeRunIdentity ||
        renderedRun.status !== "RUN_COMPLETED" ||
        renderedRun.runSettings.workflowId !== "workflow_atomic_pivots" ||
        !commonRunEditState(renderedRun.runSettings.settingsNameAndVersion) ||
        !isCommonNucleusExplainabilityCustomer(
          renderedRun.runSettings.organizationId,
        ) ||
        (Boolean(selectedCustomerId) &&
          selectedCustomerId !== renderedRun.runSettings.organizationId) ||
        (hasRenderedSourceSignalRef.current &&
          currentRenderedSourceIdentityRef.current !== sourceIdentity)
      ) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      if (isAwaitingResponse || pendingSupplyCellRef.current) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_BUSY_REASON);
      }

      const pendingCell = {
        context,
        viewContext: capture.viewContext,
        prompt: capture.prompt,
        runIdentity: renderedRunIdentity,
        sourceIdentity,
        ...(isRenderedContext ? { receiptId: context.receiptId } : {}),
        connectionReady: false,
      } as const;
      if (!hasRenderedSourceSignalRef.current) {
        currentRenderedSourceIdentityRef.current = sourceIdentity;
      }
      setPendingSupplyCell(pendingCell);
      setIsSidebarOpen(true);
      return awaitNucleusExplainStart(
        () => connectChat(renderedRun, "supply"),
        () => clearPendingSupplyCell(pendingCell),
        () => {
          if (
            !supportsSupplyCellExplainRef.current ||
            activeRunIdentityRef.current !== pendingCell.runIdentity ||
            currentRenderedSourceIdentityRef.current !==
              pendingCell.sourceIdentity
          ) {
            return false;
          }
          return replacePendingSupplyCell(
            pendingCell,
            Object.freeze({ ...pendingCell, connectionReady: true }),
          );
        },
      );
    },
    [
      activeRun,
      activeRunIdentity,
      clearPendingSupplyCell,
      connectChat,
      isAwaitingResponse,
      pendingSupplyCellRef,
      replacePendingSupplyCell,
      selectedCustomerId,
      setPendingSupplyCell,
      setIsSidebarOpen,
      supportsSupplyCellExplain,
    ],
  );

  useEffect(() => {
    if (!pendingSupplyCell) return;
    if (
      !supportsSupplyCellExplain ||
      pendingSupplyCell.runIdentity !== activeRunIdentity ||
      currentRenderedSourceIdentityRef.current !==
        pendingSupplyCell.sourceIdentity ||
      (pendingSupplyCell.context.version === "CommonSupplyCellContext.v1"
        ? !isSupportedSupplyCellRecipe(pendingSupplyCell.context)
        : pendingSupplyCell.context.surfaceId !== "supply" ||
          !isSupportedRenderedDatePivotCellContext(pendingSupplyCell.context) ||
          pendingSupplyCell.context.receiptId !== pendingSupplyCell.receiptId)
    ) {
      clearPendingSupplyCell(pendingSupplyCell);
      return;
    }
    if (
      !pendingSupplyCell.connectionReady ||
      !isConnected ||
      isConnecting ||
      isAwaitingResponse
    ) {
      return;
    }

    sendMessage(
      pendingSupplyCell.prompt,
      pendingSupplyCell.context,
      pendingSupplyCell.viewContext,
    );
    clearPendingSupplyCell(pendingSupplyCell);
  }, [
    activeRunIdentity,
    clearPendingSupplyCell,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    pendingSupplyCell,
    sendMessage,
    supportsSupplyCellExplain,
  ]);

  const cellExplanation = useMemo(
    () =>
      supportsSupplyCellExplain
        ? {
            capture: captureSupplyCell,
            onExplain: explainSupplyCell,
          }
        : undefined,
    [captureSupplyCell, explainSupplyCell, supportsSupplyCellExplain],
  );

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={{
        datasetName: "purchase_order_master",
        transformId: "purchase_order_master_date_pivot",
        folder: "outputs",
        title: "Supply",
        viewContextSurfaceId: "supply",
        dateHierarchyColumnFilter: {
          purchase_order_week: "PO week",
          purchase_order_month: "PO month",
          eta_to_warehouse_week: "ETA week",
          eta_to_warehouse_month: "ETA month",
        },
        excludedSegmentationsFilter: [],
        measureColumnFilter: {
          quantity: "quantity",
          cost: "cost",
          // Schema-gated: only shown for customers whose purchase_order_master
          // carries these physical columns (currently Cora).
          pallets: "pallets",
          eaches: "eaches",
        },
        excludedFilterColumnFilter: [],
        showControls: [
          PivotControl.DateHierarchy,
          PivotControl.DatePeriodPicker,
          PivotControl.FilterBy,
          PivotControl.Measure,
          PivotControl.SegmentSelectMulti,
          PivotControl.StoreAsDefault,
          PivotControl.Download,
          PivotControl.ShowSettings,
          PivotControl.EnableChartView,
        ],
        paramsToTransformArgs: (
          params: DatePivotParams,
        ): Record<string, any> => {
          const transformArgs = {
            min_date: params.minDate,
            max_date: params.maxDate,
            date_field: params.dateHierarchy,
            value_field: params.measure,
            group_by_fields: params.segments || [],
            filters: params.filters || [],
          };
          return transformArgs;
        },
        shadeHistory: true,
        showHeaderRow: true,
        cellExplanation,
        cellExplanationEnabled: !isAwaitingResponse && !pendingSupplyCell,
        onRenderedSourceChange: handleRenderedSourceChange,
      }}
      defaultConfig={defaultConfig}
      chartConfig={defaultChartViewerConfig}
    />
  );
};
